import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Star, Send, Copy, Check, MessageSquare, 
  Link2, Loader2, Settings, Image, ExternalLink, Zap,
  Smartphone, CheckCircle, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { demoPortfolio } from '../data/portfolioMock';

const MeuTrampoPainel = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [showEvalModal, setShowEvalModal] = useState(false);
  const [evalLink, setEvalLink] = useState(null);
  const [clientPhone, setClientPhone] = useState('');
  const [serviceName, setServiceName] = useState('');
  const [sending, setSending] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [zapiConfig, setZapiConfig] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadPortfolio();
    loadZapiConfig();
  }, [slug]);

  const loadPortfolio = async () => {
    try {
      const response = await fetch(`${API_URL}/api/portfolio/professionals/${slug}`);
      if (response.ok) {
        const data = await response.json();
        setPortfolio(data);
      } else if (slug === 'joao-reformas') {
        setPortfolio(demoPortfolio);
      }
    } catch (e) {
      if (slug === 'joao-reformas') {
        setPortfolio(demoPortfolio);
      }
    }
    setLoading(false);
  };

  const loadZapiConfig = async () => {
    try {
      // Tenta buscar config do Z-API do Agenda Cheia
      const response = await fetch(`${API_URL}/api/agenda/professionals/${slug}`);
      if (response.ok) {
        const data = await response.json();
        if (data.config?.zapi_instance && data.config?.zapi_token) {
          setZapiConfig({
            instance: data.config.zapi_instance,
            token: data.config.zapi_token,
            mode: data.config.reminder_mode
          });
        }
      }
    } catch (e) {
      console.log('Z-API config not found');
    }
  };

  const generateEvalLink = async () => {
    setShowEvalModal(true);
    setSendSuccess(false);
    setEvalLink(null);
    
    try {
      const response = await fetch(
        `${API_URL}/api/portfolio/professionals/${slug}/evaluation-link?service=${encodeURIComponent(serviceName || 'Serviço')}`,
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      
      if (response.ok) {
        const data = await response.json();
        setEvalLink(`${window.location.origin}${data.link}`);
      }
    } catch (e) {
      console.error('Erro ao gerar link:', e);
    }
  };

  const getMessage = () => {
    const businessName = portfolio?.business || portfolio?.name || 'nosso trabalho';
    return `Olá! 😊\n\nObrigado por confiar no *${businessName}*!\n\nSeu feedback é muito importante para nós. Por favor, deixe sua avaliação:\n\n${evalLink}\n\nMuito obrigado!`;
  };

  const handleSemiAutomatic = () => {
    const message = getMessage();
    const phone = clientPhone.replace(/\D/g, '');
    const url = phone 
      ? `https://wa.me/55${phone}?text=${encodeURIComponent(message)}`
      : `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const handleAutomatic = async () => {
    if (!zapiConfig || !clientPhone) return;
    
    setSending(true);
    try {
      const phone = clientPhone.replace(/\D/g, '');
      const response = await fetch(`${API_URL}/api/zapi/send-direct`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          instance: zapiConfig.instance,
          token: zapiConfig.token,
          phone: `55${phone}`,
          message: getMessage()
        })
      });
      
      if (response.ok) {
        setSendSuccess(true);
      } else {
        alert('Erro ao enviar. Tente o modo semi-automático.');
      }
    } catch (e) {
      alert('Erro ao enviar. Tente o modo semi-automático.');
    }
    setSending(false);
  };

  const copyLink = () => {
    navigator.clipboard.writeText(evalLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const averageRating = portfolio?.testimonials?.length > 0
    ? (portfolio.testimonials.reduce((acc, t) => acc + t.rating, 0) / portfolio.testimonials.length).toFixed(1)
    : '-';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!portfolio) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-slate-50">
        <AlertCircle size={48} className="text-slate-300 mb-4" />
        <h1 className="text-xl font-bold text-slate-900 mb-2">Portfólio não encontrado</h1>
        <Button onClick={() => navigate('/meutrampo/configurar')} className="bg-purple-600">
          Criar portfólio
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate('/meutrampo')} className="text-slate-500 hover:text-slate-700">
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="font-bold text-slate-900">Meu Painel</h1>
              <p className="text-sm text-slate-500">{portfolio.business || portfolio.name}</p>
            </div>
          </div>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => window.open(`/meutrampo/${slug}`, '_blank')}
          >
            <ExternalLink size={16} />
            Ver portfólio
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <div className="text-2xl font-bold text-slate-900">{portfolio.works?.length || 0}</div>
            <div className="text-sm text-slate-500">Trabalhos</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <div className="text-2xl font-bold text-slate-900">{portfolio.testimonials?.length || 0}</div>
            <div className="text-sm text-slate-500">Avaliações</div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200 text-center">
            <div className="text-2xl font-bold text-amber-500 flex items-center justify-center gap-1">
              <Star size={20} className="fill-amber-400" />
              {averageRating}
            </div>
            <div className="text-sm text-slate-500">Nota média</div>
          </div>
        </div>

        {/* Actions */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <h2 className="font-semibold text-slate-900 mb-4">Ações rápidas</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <Button 
              onClick={() => {
                setServiceName('');
                setClientPhone('');
                generateEvalLink();
              }}
              className="bg-green-500 hover:bg-green-600 h-auto py-4"
            >
              <div className="flex flex-col items-center gap-2">
                <Star size={24} />
                <span>Pedir avaliação</span>
                <span className="text-xs opacity-80">Enviar link para cliente</span>
              </div>
            </Button>
            
            <Button 
              variant="outline"
              onClick={() => {
                const url = `${window.location.origin}/meutrampo/${slug}`;
                navigator.clipboard.writeText(url);
                alert('Link do portfólio copiado!');
              }}
              className="h-auto py-4"
            >
              <div className="flex flex-col items-center gap-2">
                <Link2 size={24} />
                <span>Compartilhar portfólio</span>
                <span className="text-xs text-slate-500">Copiar link</span>
              </div>
            </Button>
          </div>
        </div>

        {/* Z-API Status */}
        <div className="bg-white rounded-xl p-6 border border-slate-200 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-900">Envio automático (Z-API)</h2>
            {zapiConfig ? (
              <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs flex items-center gap-1">
                <CheckCircle size={12} />
                Configurado
              </span>
            ) : (
              <span className="px-2 py-1 bg-slate-100 text-slate-600 rounded-full text-xs">
                Não configurado
              </span>
            )}
          </div>
          
          {zapiConfig ? (
            <p className="text-sm text-slate-600">
              Usando configuração do Agenda Cheia. Você pode enviar avaliações automaticamente.
            </p>
          ) : (
            <div className="text-sm text-slate-600">
              <p className="mb-3">Configure o Z-API no Agenda Cheia para enviar mensagens automáticas.</p>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/agenda/configurar')}
              >
                <Settings size={16} />
                Configurar Z-API
              </Button>
            </div>
          )}
        </div>

        {/* Recent Testimonials */}
        {portfolio.testimonials?.length > 0 && (
          <div className="bg-white rounded-xl p-6 border border-slate-200">
            <h2 className="font-semibold text-slate-900 mb-4">Últimas avaliações</h2>
            <div className="space-y-4">
              {portfolio.testimonials.slice(0, 3).map((t, i) => (
                <div key={i} className="flex gap-3 pb-4 border-b border-slate-100 last:border-0">
                  <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-medium">
                    {t.name?.[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-slate-900">{t.name}</span>
                      <div className="flex">
                        {[...Array(t.rating)].map((_, i) => (
                          <Star key={i} size={12} className="fill-amber-400 text-amber-400" />
                        ))}
                      </div>
                    </div>
                    <p className="text-sm text-slate-600">{t.text}</p>
                    <span className="text-xs text-slate-400">{t.service}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {/* Modal de Avaliação */}
      {showEvalModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            {sendSuccess ? (
              <div className="text-center">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle size={32} className="text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Enviado!</h3>
                <p className="text-slate-600 mb-6">A solicitação de avaliação foi enviada para o cliente.</p>
                <Button onClick={() => setShowEvalModal(false)} className="bg-purple-600">
                  Fechar
                </Button>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Pedir avaliação</h3>
                <p className="text-slate-600 text-sm mb-6">
                  Envie um link para o cliente avaliar seu trabalho
                </p>

                <div className="space-y-4 mb-6">
                  <div>
                    <Label>Serviço realizado (opcional)</Label>
                    <Input
                      value={serviceName}
                      onChange={(e) => setServiceName(e.target.value)}
                      placeholder="Ex: Reforma do banheiro"
                      className="mt-1"
                    />
                  </div>

                  <div>
                    <Label>WhatsApp do cliente</Label>
                    <Input
                      value={clientPhone}
                      onChange={(e) => setClientPhone(e.target.value)}
                      placeholder="(24) 99999-9999"
                      className="mt-1"
                    />
                    <p className="text-xs text-slate-400 mt-1">Opcional para modo semi-automático</p>
                  </div>
                </div>

                {evalLink ? (
                  <div className="space-y-3">
                    {/* Link gerado */}
                    <div className="bg-slate-50 rounded-lg p-3">
                      <p className="text-xs text-slate-500 mb-1">Link de avaliação:</p>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={evalLink}
                          readOnly
                          className="flex-1 text-xs bg-white border rounded px-2 py-1"
                        />
                        <button onClick={copyLink} className="text-purple-600">
                          {copied ? <Check size={16} /> : <Copy size={16} />}
                        </button>
                      </div>
                    </div>

                    {/* Botões de envio */}
                    <div className="grid grid-cols-2 gap-3">
                      <Button 
                        onClick={handleSemiAutomatic}
                        className="bg-green-500 hover:bg-green-600"
                      >
                        <MessageSquare size={16} />
                        WhatsApp
                      </Button>
                      
                      {zapiConfig ? (
                        <Button 
                          onClick={handleAutomatic}
                          disabled={!clientPhone || sending}
                          className="bg-blue-500 hover:bg-blue-600"
                        >
                          {sending ? (
                            <Loader2 size={16} className="animate-spin" />
                          ) : (
                            <Zap size={16} />
                          )}
                          Automático
                        </Button>
                      ) : (
                        <Button variant="outline" disabled>
                          <Zap size={16} />
                          Automático
                        </Button>
                      )}
                    </div>

                    <p className="text-xs text-center text-slate-500">
                      {zapiConfig 
                        ? 'Semi-automático: abre WhatsApp | Automático: envia via Z-API'
                        : 'Configure o Z-API para envio automático'
                      }
                    </p>
                  </div>
                ) : (
                  <div className="flex justify-center">
                    <Loader2 className="animate-spin text-purple-600" />
                  </div>
                )}

                <div className="mt-6 pt-4 border-t border-slate-200">
                  <Button variant="outline" onClick={() => setShowEvalModal(false)} className="w-full">
                    Cancelar
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MeuTrampoPainel;
